import { Select as SelectPrimitive } from "radix-ui";
import { Check, ChevronDown } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";

export interface SelectOption {
  value: string;
  label: string;
}

/** A select at the density's control height for filters and forms (ui-spec §3.1 filter bar). */
export function Select({
  value,
  onValueChange,
  options,
  label,
  placeholder,
  className,
  id,
}: {
  value: string;
  onValueChange: (v: string) => void;
  options: SelectOption[];
  /** Accessible name when there is no visible label. */
  label?: string;
  /** Shown while value is empty, so a form can require a choice instead of defaulting one. */
  placeholder?: string;
  className?: string;
  id?: string;
}) {
  return (
    <SelectPrimitive.Root value={value} onValueChange={onValueChange}>
      <SelectPrimitive.Trigger
        id={id}
        aria-label={label}
        className={cn(
          "inline-flex h-[var(--nd-control-height)] items-center justify-between gap-1.5 rounded-md border border-line-control bg-canvas px-2.5 text-ui text-fg-body",
          className,
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon>
          <ChevronDown className="size-4 text-fg-muted" aria-hidden />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className="z-[70] max-h-[var(--radix-select-content-available-height)] min-w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-line bg-popover p-1 shadow-popover"
        >
          <SelectPrimitive.Viewport className="max-h-[calc(var(--radix-select-content-available-height)-0.5rem)]">
            {options.map((o) => (
              <SelectPrimitive.Item
                key={o.value}
                value={o.value}
                className="relative flex min-h-8 cursor-default items-center rounded-md py-1 pr-8 pl-2 break-words text-ui text-fg-secondary outline-none select-none data-[highlighted]:bg-hover data-[highlighted]:text-fg-primary"
              >
                <SelectPrimitive.ItemText>{o.label}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="absolute right-2">
                  <Check className="size-4" aria-hidden />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
